import axios from 'axios'
import {useI18n} from 'i18n/I18nContext'
import {useOrganizationEvent} from 'organization/Event/OrganizationEventProvider'
import {organizationApiClient} from 'organization/organizationApiClient'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'

type Image = {
  id: number
  url: string
}

type PresignResponse = {
  url: string
  method: string
  key: string
  headers: Record<string, string>
  max_bytes: number
  expires_in: number
}

// Must stay in sync with S3PresignController::MAX_BYTES / ALLOWED_MIME on the
// API. 10 MB is the new hard cap; previous 4.5 MB was the Bref/API Gateway
// body-size ceiling that we now bypass with direct-to-S3 uploads.
const MAX_BYTES = 10 * 1024 * 1024
// Anything approaching the cap is worth warning about so organisers can
// downscale before the PUT; real event banners are comfortably <5 MB.
const SOFT_WARN_BYTES = 5 * 1024 * 1024
const ALLOWED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
]

export interface ImagesContextProps {
  images: Image[]
  save: (images: FileList) => Promise<boolean | void>
  remove: (imageId: Image['id']) => Promise<boolean | void>
  loading: boolean
}

export const ImagesContext = React.createContext<
  ImagesContextProps | undefined
>(undefined)

export default function ImagesProvider(props: {children: React.ReactElement}) {
  const {t} = useI18n()
  const apiClient = organizationApiClient()
  const {event} = useOrganizationEvent()
  const [images, setImages] = useState<Image[]>([])
  const [initLoading, setInitLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    setInitLoading(true)
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    setLoading(true)
    await apiClient
      .get(`api/event/${event?.id}/images`)
      .then((res) => setImages(res))
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => {
        setLoading(false)
        setInitLoading(false)
      })
  }

  const remove = async (imageId: Image['id']) => {
    if (loading) return

    setLoading(true)
    return apiClient
      .post(`api/event/${event?.id}/image/${imageId}`)
      .then(get)
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))
  }

  const save = async (images: FileList) => {
    if (loading) return

    setLoading(true)

    for (const image of images) {
      if (image.size > MAX_BYTES) {
        toast.warning(t('orgImages.fileTooLarge'))
        setLoading(false)
        return false
      }
      if (!ALLOWED_MIME.includes(image.type)) {
        toast.warning(t('orgImages.unsupportedType'))
        setLoading(false)
        return false
      }
      if (image.size > SOFT_WARN_BYTES) {
        toast.info(t('orgImages.largeFileHint'))
      }
    }

    for (const image of images) {
      await uploadFile(image)
    }

    get()
  }

  /**
   * Two-step direct-to-S3 flow:
   *   1. POST /api/uploads/presign → receive a short-lived PUT URL + object key.
   *   2. PUT the file straight to S3 (no Laravel/Lambda in the middle).
   *   3. POST /api/event/:id/image/confirm with the key so the API records an
   *      {@see Image} row and returns it to the gallery.
   *
   * This is what lifts the effective upload cap from ~4.5 MB (Lambda body
   * limit) to the 10 MB cap defined in S3PresignController::MAX_BYTES.
   */
  const uploadFile = async (image: File) => {
    try {
      const presign = (await apiClient.post('api/uploads/presign', {
        context: 'event_image',
        filename: image.name,
        content_type: image.type,
        size: image.size,
        event_id: event?.id,
      })) as PresignResponse

      // Raw axios — we need to bypass the organization client's default
      // Authorization header so S3 accepts the signed-URL request.
      await axios.put(presign.url, image, {
        headers: {
          ...presign.headers,
          'Content-Type': image.type,
        },
      })

      await apiClient.post(`api/event/${event?.id}/image/confirm`, {
        key: presign.key,
      })
    } catch (error: any) {
      const message =
        error?.response?.data?.message ?? t('orgImages.uploadFailed')
      toast.warning(message)
      return false
    }
  }

  return (
    <ImagesContext.Provider
      value={{
        images,
        remove,
        save,
        loading,
      }}
    >
      {initLoading && <FullPageLoader />}
      {!initLoading && props.children}
    </ImagesContext.Provider>
  )
}

export function useImages() {
  const context = React.useContext(ImagesContext)
  if (context === undefined) {
    throw new Error('useImages must be used within ImagesProvider')
  }
  return context
}
